document.querySelectorAll('[data-archive-product-gallery]').forEach((gallery) => {
  const slides = Array.from(gallery.querySelectorAll('[data-archive-gallery-slide]'));
  const thumbnails = Array.from(gallery.querySelectorAll('[data-archive-gallery-thumbnail]'));
  const stage = gallery.querySelector('[data-archive-gallery-stage]');
  const previous = gallery.querySelector('[data-archive-gallery-previous]');
  const next = gallery.querySelector('[data-archive-gallery-next]');
  const status = gallery.querySelector('[data-archive-gallery-status]');
  let activeIndex = 0;
  let requestedIndex = 0;
  let navigationRequest = 0;
  let pointerStart = null;
  let suppressNavigationClick = false;
  let activeTransition = null;

  const prepareSlide = (index, priority = 'auto') => {
    const image = slides[index]?.querySelector('img');
    if (!image) return Promise.resolve(true);

    image.loading = 'eager';
    image.setAttribute('fetchpriority', priority);

    const decode = () => {
      if (image.naturalWidth < 1) return Promise.resolve(false);
      if (typeof image.decode !== 'function') return Promise.resolve(true);
      return image.decode().then(() => true).catch(() => image.naturalWidth > 0);
    };

    if (image.complete) return decode();

    return new Promise((resolve) => {
      const finish = () => {
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        decode().then(resolve);
      };

      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
    });
  };

  const warmAdjacentSlides = (index) => {
    if (slides.length < 2) return;
    prepareSlide((index + 1) % slides.length);
    prepareSlide((index - 1 + slides.length) % slides.length);
  };

  const scheduleInitialWarmup = () => {
    const warm = () => warmAdjacentSlides(activeIndex);

    prepareSlide(activeIndex, 'high').then(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 1200 });
        return;
      }
      window.setTimeout(warm, 200);
    });
  };

  const settleSlides = (index) => {
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.classList.remove('is-entering', 'is-leaving');
      slide.hidden = !isActive;
    });
  };

  const finishActiveTransition = () => {
    if (!activeTransition) return;
    activeTransition.finish();
  };

  const transitionTo = (index) => new Promise((resolve) => {
    if (index === activeIndex) {
      settleSlides(index);
      resolve();
      return;
    }

    finishActiveTransition();

    const outgoing = slides[activeIndex];
    const incoming = slides[index];
    if (!outgoing || !incoming) {
      activeIndex = index;
      settleSlides(activeIndex);
      resolve();
      return;
    }

    let timeout;
    let firstFrame;
    let secondFrame;
    let isFinished = false;

    const finish = () => {
      if (isFinished) return;
      isFinished = true;
      window.clearTimeout(timeout);
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      activeIndex = index;
      settleSlides(activeIndex);
      if (activeTransition?.finish === finish) activeTransition = null;
      resolve();
    };

    outgoing.hidden = false;
    outgoing.classList.add('is-active', 'is-leaving');
    incoming.hidden = false;
    incoming.classList.remove('is-active', 'is-leaving');
    incoming.classList.add('is-entering');
    activeTransition = { finish };

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(finish);
    });

    timeout = window.setTimeout(finish, 120);
  });

  const show = async (nextIndex, focusThumbnail = false) => {
    if (slides.length < 1) return;

    requestedIndex = (nextIndex + slides.length) % slides.length;
    const request = ++navigationRequest;
    gallery.setAttribute('aria-busy', 'true');
    const isReady = await prepareSlide(requestedIndex, 'high');
    if (request !== navigationRequest) return;
    if (!isReady) {
      gallery.removeAttribute('aria-busy');
      return;
    }

    await transitionTo(requestedIndex);
    if (request !== navigationRequest) return;
    gallery.removeAttribute('aria-busy');
    thumbnails.forEach((thumbnail, index) => {
      const isActive = index === activeIndex;
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (status) {
      status.textContent = status.dataset.statusTemplate
        .replace('__current__', activeIndex + 1)
        .replace('__total__', slides.length);
    }

    if (focusThumbnail) {
      thumbnails[activeIndex]?.focus({ preventScroll: true });
      thumbnails[activeIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    warmAdjacentSlides(activeIndex);
  };

  thumbnails.forEach((thumbnail) => {
    thumbnail.addEventListener('click', () => {
      show(Number(thumbnail.dataset.slideIndex));
    });
  });

  previous?.addEventListener('click', (event) => {
    if (suppressNavigationClick) {
      event.preventDefault();
      return;
    }
    show(requestedIndex - 1);
  });

  next?.addEventListener('click', (event) => {
    if (suppressNavigationClick) {
      event.preventDefault();
      return;
    }
    show(requestedIndex + 1);
  });

  gallery.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    show(requestedIndex + (event.key === 'ArrowLeft' ? -1 : 1), true);
  });

  stage?.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
  }, { passive: true });

  stage?.addEventListener('pointerup', (event) => {
    if (!pointerStart || pointerStart.id !== event.pointerId) return;

    const deltaX = event.clientX - pointerStart.x;
    const deltaY = event.clientY - pointerStart.y;
    pointerStart = null;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    suppressNavigationClick = true;
    show(requestedIndex + (deltaX < 0 ? 1 : -1));
    requestAnimationFrame(() => {
      suppressNavigationClick = false;
    });
  }, { passive: true });

  stage?.addEventListener('pointercancel', () => {
    pointerStart = null;
  }, { passive: true });

  settleSlides(activeIndex);
  scheduleInitialWarmup();
});
